import React, { useEffect, useState, useCallback } from "react";
import { useNexauraData } from "../context/NexauraContext";
import { useWallet } from "../context/WalletContext";
import Card from "../components/common/Card";
import Button from "../components/common/Button";
import { fmtUsdt, toNumber } from "../utils/format";

const ROYALTY_BOARDS = [3, 4, 5, 6, 7, 8, 9, 10, 11, 12];

export default function Admin() {
  const {
    data, busy,
    fetchAdminData,
    adminSetRoyaltyRound, adminSetClubRound, adminClaimMissingPool, adminSetConfig,
    adminActivateBoard, adminRegister, adminEmergencyWithdraw, adminSetMinWithdraw,
    adminDistributeAllRoyalty, adminResetRoyaltyFund, adminResetAllRoyaltyFunds, adminResetClubFund,
    fetchIdByAddress, fetchUserAll,
  } = useNexauraData();
  const { account, refreshKey } = useWallet();

  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);

  // forms
  const [royBoard, setRoyBoard] = useState(3);
  const [royAmt, setRoyAmt] = useState("");
  const [clubAmt, setClubAmt] = useState(["", "", ""]);
  const [regAddr, setRegAddr] = useState("");
  const [regSponsor, setRegSponsor] = useState("");
  const [actUser, setActUser] = useState("");
  const [actBoard, setActBoard] = useState(1);
  const [emAmt, setEmAmt] = useState("");
  const [distPct, setDistPct] = useState("");
  const [cap, setCap] = useState("");
  const [depth, setDepth] = useState("");
  const [minW, setMinW] = useState("");
  // lookup
  const [lookup, setLookup] = useState("");
  const [lookupRes, setLookupRes] = useState(undefined); // undefined=idle, null=not found
  const [lookupBusy, setLookupBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const d = await fetchAdminData();
    setInfo(d);
    if (d) {
      setCap(String(d.config.cascadeCap));
      setDepth(String(d.config.rollupDepth));
      if (d.config.minWithdraw) setMinW(fmtUsdt(d.config.minWithdraw));
    }
    setLoading(false);
  }, [fetchAdminData]);

  useEffect(() => { load(); }, [refreshKey, busy]); // eslint-disable-line

  const pools = info?.pools;
  const club = info?.club;
  const royalty = info?.royalty || [];

  // Max distributable per user for the selected board = fund ÷ eligible count.
  const selRoyalty = royalty.find((x) => x.board === royBoard);
  const rInfo = selRoyalty?.info;
  const eligCount = rInfo ? (rInfo.eligibleCount.toNumber ? rInfo.eligibleCount.toNumber() : Number(rInfo.eligibleCount)) : 0;
  const maxPerUser = rInfo && eligCount > 0 ? toNumber(rInfo.fundAccumulated) / eligCount : 0;
  const royAmtNum = parseFloat(royAmt);
  const royExceeds = royAmt !== "" && !isNaN(royAmtNum) && royAmtNum > maxPerUser;

  const doLookup = async () => {
    const q = lookup.trim();
    if (!q) return;
    setLookupBusy(true);
    let id = 0;
    if (q.startsWith("0x") && q.length === 42) id = await fetchIdByAddress(q);
    else if (/^\d+$/.test(q)) id = Number(q);
    if (!id) { setLookupRes(null); setLookupBusy(false); return; }
    const res = await fetchUserAll(id);
    setLookupRes(res ? { id, ...res } : null);
    setLookupBusy(false);
  };

  const perUser = (r) => {
    const cnt = r.eligibleCount.toNumber ? r.eligibleCount.toNumber() : Number(r.eligibleCount);
    if (!cnt) return "—";
    try { return `${fmtUsdt(r.fundAccumulated.div(cnt))} USDT`; } catch { return "—"; }
  };

  return (
    <div className="screen">
      <div className="screen-title">{data.isOwner ? "Owner" : "Admin"} Panel</div>
      <div className="screen-sub">{account ? `${account.slice(0, 6)}…${account.slice(-4)}` : ""}</div>

      {/* 1. ROYALTY */}
      <div className="admin-section-h">Royalty Pools</div>
      <Card glow="gold" className="section-gap">
        <div className="field" style={{ marginBottom: 12 }}>
          <label>Select royalty board</label>
          <select className="admin-select" value={royBoard} onChange={(e) => setRoyBoard(Number(e.target.value))}>
            {ROYALTY_BOARDS.map((b) => <option key={b} value={b}>Board {b}</option>)}
          </select>
        </div>

        {loading ? (
          <div className="empty">Loading…</div>
        ) : (() => {
          const sel = royalty.find((x) => x.board === royBoard);
          if (!sel) return <div className="empty">No data.</div>;
          const r = sel.info;
          return (
            <div className="adm-block" style={{ borderBottom: "none", paddingTop: 0 }}>
              <div className="flex-between">
                <span style={{ fontWeight: 800 }}>Board {royBoard}</span>
                <span className="muted" style={{ fontSize: 12 }}>Round #{r.round.toString()}</span>
              </div>
              <div className="adm-grid">
                <div><span className="ag-k">Accumulated</span><span className="ag-v">{fmtUsdt(r.fundAccumulated)}</span></div>
                <div><span className="ag-k">Eligible</span><span className="ag-v">{r.eligibleCount.toString()}</span></div>
                <div><span className="ag-k">Amt / user</span><span className="ag-v">{fmtUsdt(r.latestAmountPerUser)}</span></div>
                <div><span className="ag-k">Fund / eligible</span><span className="ag-v">{perUser(r)}</span></div>
              </div>
            </div>
          );
        })()}

        <div className="admin-divider" />
        <div className="field">
          <label>Amount per eligible user (USDT)</label>
          <input
            inputMode="decimal"
            className={royExceeds ? "input-error" : ""}
            value={royAmt}
            onChange={(e) => setRoyAmt(e.target.value)}
            placeholder="e.g. 1.5"
          />
        </div>
        <div className="max-hint">
          Max per user: <b>{maxPerUser > 0 ? `${maxPerUser.toLocaleString(undefined, { maximumFractionDigits: 6 })} USDT` : "—"}</b>
          <span className="muted"> (fund ÷ {eligCount} eligible)</span>
        </div>
        {royExceeds && (
          <div className="reg-warn" style={{ margin: "10px 0 12px" }}>
            ⚠ Amount exceeds the max. It cannot be more than {maxPerUser.toLocaleString(undefined, { maximumFractionDigits: 6 })} USDT (fund ÷ eligible).
          </div>
        )}
        <Button size="sm" loading={busy} disabled={!royAmt || royExceeds} onClick={() => adminSetRoyaltyRound(royBoard, royAmt)}>Set Round for Board {royBoard}</Button>

        <div className="admin-divider" />
        <div className="field"><label>Distribute ALL royalty boards — percent of fund (%)</label>
          <input inputMode="decimal" value={distPct} onChange={(e) => setDistPct(e.target.value)} placeholder="e.g. 50 or 100" />
        </div>
        <Button size="sm" variant="blue" loading={busy} disabled={!distPct} onClick={() => adminDistributeAllRoyalty(distPct)}>Distribute All Royalty</Button>

        <div className="row" style={{ gap: 8, marginTop: 12 }}>
          <Button size="sm" variant="secondary" loading={busy} onClick={() => adminResetRoyaltyFund(royBoard)}>Reset B{royBoard} Fund</Button>
          <Button size="sm" variant="secondary" loading={busy} onClick={adminResetAllRoyaltyFunds}>Reset All Funds</Button>
        </div>
      </Card>

      {/* 2. CLUBS */}
      <div className="admin-section-h">Clubs</div>
      <Card glow="blue" className="section-gap">
        {loading || !club ? <div className="empty">Loading…</div> : (
          [
            { name: "Gold", round: club.goldRound, latest: club.goldLatest, count: club.goldCount, fund: club.goldFund, id: 0, variant: "gold" },
            { name: "Diamond", round: club.diamondRound, latest: club.diamondLatest, count: club.diamondCount, fund: club.diamondFund, id: 1, variant: "blue" },
            { name: "Global", round: club.globalRound, latest: club.globalLatest, count: club.globalCount, fund: club.globalFund, id: 2, variant: "blue" },
          ].map((c) => {
            const cCount = c.count.toNumber ? c.count.toNumber() : Number(c.count);
            const cMax = cCount > 0 ? toNumber(c.fund) / cCount : 0;
            const cVal = parseFloat(clubAmt[c.id]);
            const cExceeds = clubAmt[c.id] !== "" && !isNaN(cVal) && cVal > cMax;
            return (
            <div className="adm-block" key={c.id}>
              <div className="flex-between">
                <span style={{ fontWeight: 800 }}>{c.name} Club</span>
                <span className="muted" style={{ fontSize: 12 }}>Round #{c.round.toString()}</span>
              </div>
              <div className="adm-grid">
                <div><span className="ag-k">Accumulated</span><span className="ag-v">{fmtUsdt(c.fund)}</span></div>
                <div><span className="ag-k">Eligible</span><span className="ag-v">{c.count.toString()}</span></div>
                <div><span className="ag-k">Amt / user</span><span className="ag-v">{fmtUsdt(c.latest)}</span></div>
                <div>
                  <span className="ag-k">Max / user</span>
                  <span className="ag-v">{cMax > 0 ? `${cMax.toLocaleString(undefined, { maximumFractionDigits: 6 })}` : "—"}</span>
                </div>
              </div>
              <div className="row" style={{ gap: 8, marginTop: 4 }}>
                <input className={`admin-inline ${cExceeds ? "input-error" : ""}`} inputMode="decimal" placeholder="amount / user"
                  value={clubAmt[c.id]} onChange={(e) => setClubAmt((p) => p.map((x, i) => i === c.id ? e.target.value : x))} />
                <Button size="sm" variant={c.variant} loading={busy} disabled={!clubAmt[c.id] || cExceeds} onClick={() => adminSetClubRound(c.id, clubAmt[c.id])}>Set Round</Button>
                <Button size="sm" variant="secondary" loading={busy} onClick={() => adminResetClubFund(c.id)}>Reset</Button>
              </div>
              {cExceeds && (
                <div className="reg-warn" style={{ margin: "10px 0 0" }}>
                  ⚠ Max per user is {cMax.toLocaleString(undefined, { maximumFractionDigits: 6 })} USDT (fund ÷ eligible). Enter a lower amount.
                </div>
              )}
            </div>
          );})
        )}
      </Card>

      {/* 3. REGISTER + ACTIVATE */}
      <div className="admin-section-h">Register & Activate</div>
      <Card className="section-gap">
        <div className="field"><label>Register user — wallet address</label>
          <input placeholder="0x…" value={regAddr} onChange={(e) => setRegAddr(e.target.value.trim())} />
        </div>
        <div className="field"><label>Sponsor ID (optional)</label>
          <input inputMode="numeric" value={regSponsor} onChange={(e) => setRegSponsor(e.target.value.replace(/\D/g, ""))} placeholder="default if blank" />
        </div>
        <Button size="sm" loading={busy} disabled={!regAddr} onClick={() => adminRegister(regAddr, regSponsor)}>Register User (free)</Button>
        <div className="admin-divider" />
        <div className="field"><label>Activate board — User ID</label>
          <input inputMode="numeric" value={actUser} onChange={(e) => setActUser(e.target.value.replace(/\D/g, ""))} placeholder="user id" />
        </div>
        <div className="field"><label>Board</label>
          <select className="admin-select" value={actBoard} onChange={(e) => setActBoard(Number(e.target.value))}>
            {Array.from({ length: 12 }, (_, i) => i + 1).map((b) => <option key={b} value={b}>Board {b}</option>)}
          </select>
        </div>
        <Button size="sm" variant="secondary" loading={busy} disabled={!actUser} onClick={() => adminActivateBoard(actUser, actBoard)}>Activate Board (free)</Button>
      </Card>

      {/* 4. USER LOOKUP */}
      <div className="admin-section-h">User Lookup</div>
      <Card className="section-gap">
        <div className="row" style={{ gap: 8 }}>
          <input className="admin-inline" placeholder="User ID or 0x address" value={lookup} onChange={(e) => setLookup(e.target.value)} />
          <Button size="sm" loading={lookupBusy} disabled={!lookup} onClick={doLookup}>Lookup</Button>
        </div>
        {lookupRes === null && <div className="empty" style={{ padding: "16px 8px" }}>No user found.</div>}
        {lookupRes && lookupRes.details && (
          <div style={{ marginTop: 12 }}>
            <div className="row"><span className="r-label">User ID</span><span className="r-value">#{lookupRes.id}</span></div>
            <div className="row"><span className="r-label">Address</span><span className="r-value" style={{ fontSize: 12 }}>{lookupRes.details.account.slice(0, 8)}…{lookupRes.details.account.slice(-6)}</span></div>
            <div className="row"><span className="r-label">Sponsor</span><span className="r-value">#{lookupRes.details.referrer}</span></div>
            <div className="row"><span className="r-label">Directs</span><span className="r-value">{lookupRes.details.totalDirectReferrals}</span></div>
            <div className="row"><span className="r-label">Total income</span><span className="r-value">{fmtUsdt(lookupRes.details.totalIncome)} USDT</span></div>
            <div className="row"><span className="r-label">Claimable</span><span className="r-value">{fmtUsdt(lookupRes.details.claimableAmount)} USDT</span></div>
            <div className="row"><span className="r-label">Withdrawn</span><span className="r-value">{fmtUsdt(lookupRes.details.totalClaimed)} USDT</span></div>
            <div style={{ marginTop: 10 }}>
              <span className="ag-k">Boards</span>
              <div className="lookup-boards">
                {lookupRes.packages.map((on, i) => (
                  <span key={i} className={`lb ${on ? "on" : ""}`}>{i + 1}</span>
                ))}
              </div>
            </div>
          </div>
        )}
      </Card>

      {/* 5. MISSING POOL — owner only */}
      {data.isOwner && (
        <>
          <div className="admin-section-h">Missing Pool <span className="owner-tag">owner</span></div>
          <Card className="section-gap">
            <div className="row"><span className="r-label">Missing pool</span><span className="r-value">{pools ? fmtUsdt(pools.missing) : "…"} USDT</span></div>
            <div className="muted" style={{ fontSize: 11.5, margin: "8px 0 12px" }}>Funds are sent to your connected wallet.</div>
            <Button size="sm" variant="secondary" loading={busy} onClick={adminClaimMissingPool}>Claim Full Missing Pool</Button>

            <div className="admin-divider" />
            <div className="field"><label>Emergency withdraw — amount (USDT)</label>
              <input inputMode="decimal" value={emAmt} onChange={(e) => setEmAmt(e.target.value)} placeholder="amount" />
            </div>
            <Button size="sm" variant="secondary" loading={busy} disabled={!emAmt} onClick={() => adminEmergencyWithdraw(emAmt)}>Emergency Withdraw</Button>
          </Card>
        </>
      )}

      {/* PLATFORM SETTINGS (admin) */}
      <div className="admin-section-h">Platform Settings</div>
      <Card className="section-gap">
        <div className="row"><span className="r-label">Current min withdraw</span><span className="r-value">{info?.config?.minWithdraw ? fmtUsdt(info.config.minWithdraw) : "…"} USDT</span></div>
        <div className="field" style={{ marginTop: 10 }}><label>Set minimum withdrawal (USDT)</label>
          <input inputMode="decimal" value={minW} onChange={(e) => setMinW(e.target.value)} placeholder="e.g. 5" />
        </div>
        <Button size="sm" variant="secondary" loading={busy} disabled={!minW} onClick={() => adminSetMinWithdraw(minW)}>Update Min Withdraw</Button>
      </Card>

      {/* OWNER-ONLY: CONFIG */}
      {data.isOwner && (
        <>
          <div className="admin-section-h">Owner Config <span className="owner-tag">owner</span></div>
          <Card className="section-gap">
            <div className="field"><label>Cascade cap</label>
              <input inputMode="numeric" value={cap} onChange={(e) => setCap(e.target.value.replace(/\D/g, ""))} />
            </div>
            <Button size="sm" variant="secondary" loading={busy} disabled={cap === ""} onClick={() => adminSetConfig("cascadeCap", Number(cap))}>Update Cascade Cap</Button>
            <div className="field" style={{ marginTop: 12 }}><label>Rollup depth</label>
              <input inputMode="numeric" value={depth} onChange={(e) => setDepth(e.target.value.replace(/\D/g, ""))} />
            </div>
            <Button size="sm" variant="secondary" loading={busy} disabled={depth === ""} onClick={() => adminSetConfig("rollupDepth", Number(depth))}>Update Rollup Depth</Button>
          </Card>
        </>
      )}
    </div>
  );
}
